import axios, { type InternalAxiosRequestConfig } from "axios";
import endevreID, {
  createTokenBroker,
  isOidcAuthorizationResponseError,
  type ExchangeResult,
  type TokenBroker,
} from "@endevre/id-client";

import { TicketingApiUri } from "../constants";

export type AdventAuthMode = "legacy" | "oidc";

const DEFAULT_RETURN_PATH = "/account?from=login";
const OIDC_CANCELLATION_PATH = "/";
const AUTH_CALLBACK_PATH = "/auth";
const LOGOUT_CALLBACK_PATH = "/api/auth/logout.php";
const MAX_RETURN_PATH_LENGTH = 512;

export const clientID = process.env.REACT_APP_ENDEVRE_CLIENT_ID || "";

export function getAdventAuthMode(): AdventAuthMode {
  const mode = String(
    process.env.REACT_APP_ENDEVRE_AUTH_MODE || "legacy"
  ).trim().toLowerCase();
  if (mode !== "legacy" && mode !== "oidc") {
    throw new Error("REACT_APP_ENDEVRE_AUTH_MODE must be legacy or oidc");
  }
  return mode;
}

function requestOrigin(): string {
  const origin = window.location.origin;
  const parsed = new URL(origin);
  const isLoopback =
    parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  if (parsed.protocol !== "https:" && !(isLoopback && parsed.protocol === "http:")) {
    throw new Error("Endevre ID sign-in requires HTTPS");
  }
  if (parsed.username || parsed.password || parsed.pathname !== "/") {
    throw new Error("The current Advent origin is invalid");
  }
  return parsed.origin;
}

export function sanitizeReturnPath(value?: string | null): string {
  if (!value) return DEFAULT_RETURN_PATH;
  if (
    value.length > MAX_RETURN_PATH_LENGTH ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    Array.from(value).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    })
  ) {
    return DEFAULT_RETURN_PATH;
  }

  try {
    const target = new URL(value, requestOrigin());
    if (target.origin !== requestOrigin() || target.hash) {
      return DEFAULT_RETURN_PATH;
    }
    return `${target.pathname}${target.search}`;
  } catch {
    return DEFAULT_RETURN_PATH;
  }
}

export function createAdventAuthClient() {
  const mode = getAdventAuthMode();
  return endevreID.createBrowserClient(clientID, {
    protocol: mode,
    cookiePrefix: "endevre_id_",
    cookieSecure: window.location.protocol === "https:",
    cookieSameSite: "Lax",
    defaultOrigin: requestOrigin(),
    defaultReturnURL: requestOrigin(),
    ...(mode === "oidc"
      ? {
          oidc: {
            redirectUri: `${requestOrigin()}${AUTH_CALLBACK_PATH}`,
            scopes: ["openid", "offline_access", "profile", "email"],
            clientType: "public" as const,
            tokenEndpointAuthMethod: "none" as const,
          },
        }
      : {}),
  });
}

export function buildLoginUrl(returnPath?: string | null): string {
  if (!clientID) {
    throw new Error("Endevre ID client ID is not configured");
  }
  const origin = requestOrigin();
  const client = createAdventAuthClient();
  if (getAdventAuthMode() === "legacy") {
    return client.createAuthUrl({
      finalredirecturi: `${origin}${sanitizeReturnPath(returnPath)}`,
      redirecturis: [`${origin}${AUTH_CALLBACK_PATH}`],
    });
  }

  return client.createAuthUrl({
    finalredirecturi: `${origin}${AUTH_CALLBACK_PATH}`,
    scope: ["openid", "offline_access", "profile", "email"],
    return_path: sanitizeReturnPath(returnPath),
  });
}

export async function storeOidcSession(result: {
  token?: string;
  refresh?: string;
  expire?: number;
  scopes?: string | string[];
  idToken?: string;
}): Promise<void> {
  if (
    !result.token ||
    !result.refresh ||
    !result.expire ||
    !result.idToken
  ) {
    throw new Error("Endevre ID returned an incomplete OIDC session");
  }

  const response = await fetch("/api/auth/session.php", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      clientId: clientID,
      token: result.token,
      refresh: result.refresh,
      expire: result.expire,
      scopes: result.scopes || [],
      idToken: result.idToken,
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      typeof body?.error === "string"
        ? body.error
        : "Advent could not establish the OIDC session"
    );
  }
}

export async function handleOidcCallback(callbackUrl = window.location.href): Promise<string> {
  if (getAdventAuthMode() !== "oidc") {
    throw new Error("OIDC callback handling is not enabled");
  }
  const client = createAdventAuthClient();
  let result: ExchangeResult;
  try {
    result = await client.exchange({ callbackUrl });
  } catch (error) {
    if (
      isOidcAuthorizationResponseError(error)
      && error.oauthError === "access_denied"
    ) {
      return OIDC_CANCELLATION_PATH;
    }
    throw error;
  }
  await storeOidcSession(result);

  const callback = new URL(callbackUrl);
  return sanitizeReturnPath(callback.searchParams.get("return_path"));
}

export function buildEndevreLogoutUrl(body: {
  idToken?: unknown;
  state?: unknown;
  postLogoutRedirectUri?: unknown;
}): string {
  if (
    typeof body.idToken !== "string" ||
    typeof body.state !== "string" ||
    typeof body.postLogoutRedirectUri !== "string"
  ) {
    throw new Error("Advent received an invalid logout session");
  }

  const expectedCallback = `${requestOrigin()}${LOGOUT_CALLBACK_PATH}`;
  if (body.postLogoutRedirectUri !== expectedCallback) {
    throw new Error("Advent received an invalid logout callback");
  }
  const logoutUrl = createAdventAuthClient().createLogoutUrl({
    idToken: body.idToken,
    state: body.state,
    finalredirecturi: body.postLogoutRedirectUri,
  });
  const parsedLogoutUrl = new URL(logoutUrl);
  if (
    parsedLogoutUrl.origin !== "https://id.endevre.com" ||
    parsedLogoutUrl.pathname !== "/oauth/logout"
  ) {
    throw new Error("Endevre ID returned an invalid logout URL");
  }
  return logoutUrl;
}

type LogoutNavigation = Pick<Location, "assign" | "replace">;

export async function startEndevreLogout(
  navigation: LogoutNavigation = window.location
): Promise<void> {
  if (getAdventAuthMode() !== "oidc") {
    const client = createAdventAuthClient();
    client.logout({ returnURL: "/" });
    return;
  }

  const response = await fetch("/api/auth/logout-start.php", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    body: "{}",
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      typeof body?.error === "string"
        ? body.error
        : "Advent could not start Endevre ID logout"
    );
  }
  if (body?.localOnly === true) {
    navigation.replace("/");
    return;
  }
  const logoutUrl = buildEndevreLogoutUrl(body || {});
  navigation.assign(logoutUrl);
}

let axiosInterceptor: number | null = null;

export async function authorizeTicketingApiRequest(
  config: InternalAxiosRequestConfig,
  broker: TokenBroker
): Promise<InternalAxiosRequestConfig> {
  const apiOrigin = new URL(TicketingApiUri).origin;
  const target = new URL(
    config.url || "",
    config.baseURL || window.location.origin
  );
  if (target.origin !== apiOrigin) {
    return config;
  }

  const token = await broker.getAccessToken();
  if (!token) {
    return config;
  }
  const bearer = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  config.headers.set("Authorization", bearer);
  return config;
}

export function configureOidcApiAuthorization(): void {
  if (axiosInterceptor !== null || getAdventAuthMode() !== "oidc") {
    return;
  }
  const broker = createAdventTokenBroker();

  axiosInterceptor = axios.interceptors.request.use((config) =>
    authorizeTicketingApiRequest(config, broker)
  );
}

export function createAdventTokenBroker(): TokenBroker {
  return createTokenBroker({
    refreshEndpoint: "/api/auth/refresh.php",
    refreshThresholdMs: 5 * 60 * 1000,
    cookiePrefix: "endevre_id_",
  });
}
